import './TrialBar.scss';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../contexts/account/AccountContexts';
import useBilling from '../../hooks/account/UseBilling';
import { NavLink } from 'react-router-dom';
import { PRICING } from '../../routes/path';
import { getTrialDays } from '../../utils/utils';

interface TrialBarProps {
  position?: string;
}

const TrialBar: React.FC<TrialBarProps> = ({ position = 'bottom' }) => {
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const canShowBilling = useBilling();

  // Show payment suspended banner even for PAID accounts
  if (accountResponse?.paymentSuspended) {
    return (
      <div className={`trial-bar trial-bar--${position}`}>
        <div className="trial-bar__container trial-bar__container--info">
          {t('paymentSuspendedInfo')}
        </div>
      </div>
    );
  }

  if (!accountResponse || accountResponse.accountStatus === 'PAID' || !canShowBilling) {
    return <></>;
  }

  if (accountResponse.accountStatus === 'TRIAL' && !accountResponse?.showTrialNotification) {
    return <></>;
  }

  const getTrialDaysText = (): string => {
    const daysLeft = getTrialDays(accountResponse);
    const isReactivation = accountResponse.reactivatedAt != null;
    if (daysLeft === 1) {
      return t(isReactivation ? 'reactivationExpiredOneDay' : 'trialExpiredOneDay');
    }

    if (daysLeft > 0) {
      return t(isReactivation ? 'reactivationExpiredLeft' : 'trialExpiredLeft', { daysLeft });
    }

    if (daysLeft < 0) {
      return t(isReactivation ? 'reactivationExpired' : 'trialExpired');
    }

    return t(isReactivation ? 'reactivationExpiredToday' : 'trialExpiredToday');
  };

  const getBarStyle = (): string => {
    if (getTrialDays(accountResponse) === 0 || accountResponse.accountStatus === 'OVERDUE') {
      return 'trial-bar__container--warning';
    }

    if (
      getTrialDays(accountResponse) < 0 ||
      ['DISABLED', 'BLOCKED'].includes(accountResponse.accountStatus)
    ) {
      return 'trial-bar__container--error';
    }

    return '';
  };

  const getTrialBarText = (): string => {
    if (['DISABLED', 'BLOCKED'].includes(accountResponse.accountStatus)) {
      return t('disabledAccount');
    }

    if (accountResponse.accountStatus === 'OVERDUE') {
      return t('overdueAccount');
    }

    return getTrialDaysText();
  };

  return (
    <div className={`trial-bar trial-bar--${position}`}>
      <div className={`trial-bar__container ${getBarStyle()}`}>
        {getTrialBarText()}
        <NavLink className="btn btn--warning btn--xxs" to={PRICING}>
          {t('upgrade')}
        </NavLink>
      </div>
    </div>
  );
};

export default TrialBar;
